import clsx from 'clsx'
import React, {useRef} from 'react'

import * as classes from './FloatingBox.classes'
import {useFloating} from './hooks/useFloating'

export const enum StickyTo {
  BLOCK_END = 'blockEnd',
  BLOCK_START = 'blockStart',
}

type FloatingBoxProps = {
  children: React.ReactElement
  className?: string
  height: string
  stickyTo: StickyTo
}

/**
 * Компонент, делающий своих `children` `sticky`-конентом относительно всего пользовательского экрана
 *
 * Данный компонент решает конкретную задачу - позволяет делать контент `sticky` относительно всего экрана пользователя
 * на любом уровне вложенности, а не только относительно своего родителя.
 * Под капотом используется `fixed`-позиционирование, благодаря чему это стало возможным
 *
 * Теперь можно, например, закастомить фон пространства, выделяемого под `sticky`-контент
 *
 * Реализованы два вида позиционирования (проп `stickyTo`):
 * - `StickyTo.BLOCK_END` - элемент прилепляется к нижней части экрана, когда соприкасается с ней
 * - `StickyTo.BLOCK_START` - элемент прилепляется к верхней части экрана, когда соприкасается с ней,
 * @param param0 пропы компонента
 * @returns Компонент, который делает `children` `sticky`-конентом относительно всего экрана пользователя
 */
export function FloatingBox({
  children,
  className,
  height,
  stickyTo,
}: FloatingBoxProps): React.ReactElement<FloatingBoxProps> {
  const ref = useRef<HTMLDivElement>(null)
  const isFloating = useFloating(ref, stickyTo)

  return (
    <div ref={ref} className={clsx(classes.wrapper, className)} style={{height}}>
      {React.cloneElement(children, {
        className: clsx(classes.floating, {[classes[stickyTo]]: isFloating}, children.props.className),
      })}
    </div>
  )
}
